import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { convertMarkdownToHtml, hasHtml, normalizeDescriptionForEditor } from './markdown-editor.utils';

@Component({
    selector: 'app-markdown-editor',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './markdown-editor.component.html',
    styleUrl: './markdown-editor.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MarkdownEditorComponent),
            multi: true,
        },
    ],
})
export class MarkdownEditorComponent implements ControlValueAccessor {
    @Input() placeholder = 'Enter description';
    @ViewChild('descriptionTextarea') descriptionTextarea?: ElementRef<HTMLTextAreaElement>;

    activeEditorTab: 'write' | 'preview' = 'write';
    previewContent: SafeHtml = '';
    value = '';
    disabled = false;

    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
    ) {}

    writeValue(value: string | null): void {
        this.value = normalizeDescriptionForEditor(value);
        this.updatePreview();
        this.cdr.detectChanges();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    setEditorTab(tab: 'write' | 'preview', event?: Event) {
        event?.preventDefault();
        event?.stopPropagation();
        this.activeEditorTab = tab;
        this.updatePreview();
        this.cdr.detectChanges();
    }

    applyMarkdown(prefix: string, suffix = '', placeholder = '') {
        const textarea = this.descriptionTextarea?.nativeElement;
        if (!textarea || this.disabled) {
            return;
        }

        const start = textarea.selectionStart ?? 0;
        const end = textarea.selectionEnd ?? 0;
        const selectedText = this.value.slice(start, end) || placeholder;
        const updatedValue =
            this.value.slice(0, start) +
            prefix +
            selectedText +
            suffix +
            this.value.slice(end);

        this.setValue(updatedValue);

        const cursorStart = start + prefix.length;
        const cursorEnd = cursorStart + selectedText.length;

        queueMicrotask(() => {
            textarea.focus();
            textarea.setSelectionRange(cursorStart, cursorEnd);
        });
    }

    insertMarkdownBlock(prefix: string, placeholder: string) {
        const textarea = this.descriptionTextarea?.nativeElement;
        if (!textarea || this.disabled) {
            return;
        }

        const start = textarea.selectionStart ?? 0;
        const end = textarea.selectionEnd ?? 0;
        const selectedText = this.value.slice(start, end);
        const blockText = selectedText
            ? selectedText
                  .split('\n')
                  .map((line: string) => `${prefix}${line}`)
                  .join('\n')
            : `${prefix}${placeholder}`;
        const updatedValue = this.value.slice(0, start) + blockText + this.value.slice(end);

        this.setValue(updatedValue);

        queueMicrotask(() => {
            textarea.focus();
            textarea.setSelectionRange(start, start + blockText.length);
        });
    }

    undoDescription() {
        this.setValue('');
    }

    onDescriptionInput(event: Event) {
        const target = event.target as HTMLTextAreaElement;
        this.setValue(target.value);
    }

    onBlur() {
        this.onTouched();
    }

    private setValue(value: string) {
        this.value = value;
        this.onChange(this.value);
        this.updatePreview();
    }

    private updatePreview() {
        this.previewContent = this.sanitizer.bypassSecurityTrustHtml(
            hasHtml(this.value) ? this.value : convertMarkdownToHtml(this.value),
        );
    }
}
